/**
 * DEFCAT-R2-04 — global component-group (L0) name source for the catalogue
 * editor trees.
 *
 * The catalogue location entries carry `componentGroupId` (a UUID into
 * `companies_db.global_component_groups`); the human-readable L0 NAMES live in
 * the assets domain, not the catalogue payload. They are fetched from
 * `GET /api/assets/v1/component-groups`. The host's `useApi()` axios instance is
 * base-pinned to `/api/lyse/v1`, so we pass a per-request `baseURL` override
 * (`getAssetsBaseUrl()`) — the same cross-namespace pattern `taxonomyQueries.ts`
 * uses for the taxonomy gateway. (apps/lyse has its own copy of this hook for
 * the defect drawer; we can't import across app packages, so this mirrors it.)
 */
import { useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { getAssetsBaseUrl } from "@aeriosiq/auth-client";

export interface ComponentGroupItem {
  id: string;
  groupCode: string;
  name: Record<string, string>;
}
export interface ListComponentGroupsResponse {
  items: ComponentGroupItem[];
  total: number;
}

export const componentGroupsKeys = {
  all: ["component-groups"] as const,
};

/** The handful of `global_component_groups`. Near-static catalogue data. */
export function useComponentGroups(): UseQueryResult<ListComponentGroupsResponse> {
  const api = useApi();
  return useQuery<ListComponentGroupsResponse>({
    queryKey: componentGroupsKeys.all,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await api.get<ListComponentGroupsResponse>(
        "/component-groups",
        { baseURL: getAssetsBaseUrl() },
      );
      return res.data;
    },
  });
}

/**
 * Convenience resolver: `componentGroupId → display name`. Returns a stable
 * function suitable to pass into `buildLocationTreeWithL0`. Unknown ids
 * resolve to `null`.
 *
 * Language: ENGLISH-FIRST (en → first non-empty), matching `locText` — the
 * whole catalogue editor renders `.en` until a real locale store exists.
 * The first cut preferred `navigator.language`, which made L0 names render
 * German on a de-* browser while every other label was English (Christian,
 * 2026-06-04 morning review). Do NOT key display language off the browser
 * here; when a locale store lands, switch locText + this together.
 */
export function useComponentGroupNameResolver(): (
  componentGroupId: string,
) => string | null {
  const { data } = useComponentGroups();
  return useMemo(() => {
    const byId = new Map<string, ComponentGroupItem>();
    for (const g of data?.items ?? []) byId.set(g.id, g);
    return (componentGroupId: string): string | null => {
      const g = byId.get(componentGroupId);
      if (!g) return null;
      const en = g.name.en?.trim();
      if (en && en.length > 0) return en;
      for (const key of Object.keys(g.name)) {
        const candidate = g.name[key]?.trim();
        if (candidate && candidate.length > 0) return candidate;
      }
      return null;
    };
  }, [data]);
}
